/**
 * MIT License
 *
 * Copyright (c) 2023–Present PPResume (https://ppresume.com)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */

import { get } from 'lodash-es'

import type {
  Country,
  Degree,
  Fluency,
  Language,
  Level,
  LocaleLanguage,
  OrderableSectionID,
} from '@/models'

import {
  EnglishCountryNames,
  SimplifiedChineseCountryNames,
  SpanishCountryNames,
  TraditionalChineseCountryHKNames,
  TraditionalChineseCountryTWNames,
} from '@/models'

/** Defines the structure for translated terms for a single language. */
type OptionTranslation = {
  /** Translations for country names. */
  countries: Record<Country, string>
  /** Translations for degree types. */
  degrees: Record<Degree, string>
  /** Translations for language fluency levels. */
  fluency: Record<Fluency, string>
  /** Translations for language names. */
  languages: Record<Language, string>
  /** Translations for resume section titles. */
  sections: Record<OrderableSectionID, string>
  /** Translations for skill proficiency levels. */
  skills: Record<Level, string>
}

type OptionCategory = keyof OptionTranslation

/** The structure containing translations for all supported languages. */
type OptionsTranslations = Record<LocaleLanguage, OptionTranslation>

/**
 * Retrieves the translated terms for a specific locale language.
 *
 * Includes translations for degrees, languages, fluencies, countries, section
 * titles, levels, and other specific terms.
 *
 * @param language - The desired locale language. If undefined, defaults to
 * English.
 * @returns An object containing the translated terms for the specified
 * language.
 */
export function getOptionTranslation<K extends OptionCategory>(
  language: LocaleLanguage,
  category: K,
  option: keyof OptionTranslation[K]
): string {
  const optionsTranslations: OptionsTranslations = {
    en: {
      countries: EnglishCountryNames,
      degrees: {
        'Middle School': 'Middle School',
        'High School': 'High School',
        Diploma: 'Diploma',
        Associate: 'Associate',
        Bachelor: 'Bachelor',
        Master: 'Master',
        Doctor: 'Doctor',
      },
      languages: {
        Afrikaans: 'Afrikaans',
        Albanian: 'Albanian',
        Amharic: 'Amharic',
        Arabic: 'Arabic',
        Azerbaijani: 'Azerbaijani',
        Belarusian: 'Belarusian',
        Bengali: 'Bengali',
        Bhojpuri: 'Bhojpuri',
        Bulgarian: 'Bulgarian',
        Burmese: 'Burmese',
        Cantonese: 'Cantonese',
        Catalan: 'Catalan',
        Chinese: 'Chinese',
        Croatian: 'Croatian',
        Czech: 'Czech',
        Danish: 'Danish',
        Dutch: 'Dutch',
        English: 'English',
        Estonian: 'Estonian',
        Farsi: 'Farsi',
        Filipino: 'Filipino',
        Finnish: 'Finnish',
        French: 'French',
        German: 'German',
        Greek: 'Greek',
        Gujarati: 'Gujarati',
        Hausa: 'Hausa',
        Hebrew: 'Hebrew',
        Hindi: 'Hindi',
        Hungarian: 'Hungarian',
        Icelandic: 'Icelandic',
        Igbo: 'Igbo',
        Indonesian: 'Indonesian',
        Irish: 'Irish',
        Italian: 'Italian',
        Japanese: 'Japanese',
        Javanese: 'Javanese',
        Kazakh: 'Kazakh',
        Khmer: 'Khmer',
        Korean: 'Korean',
        Lahnda: 'Lahnda',
        Latvian: 'Latvian',
        Lithuanian: 'Lithuanian',
        Malay: 'Malay',
        Mandarin: 'Mandarin',
        Marathi: 'Marathi',
        Nepali: 'Nepali',
        Norwegian: 'Norwegian',
        Oromo: 'Oromo',
        Pashto: 'Pashto',
        Polish: 'Polish',
        Portuguese: 'Portuguese',
        Romanian: 'Romanian',
        Russian: 'Russian',
        Serbian: 'Serbian',
        Shona: 'Shona',
        Sinhala: 'Sinhala',
        Slovak: 'Slovak',
        Slovene: 'Slovene',
        Somali: 'Somali',
        Spanish: 'Spanish',
        Sundanese: 'Sundanese',
        Swahili: 'Swahili',
        Swedish: 'Swedish',
        Tagalog: 'Tagalog',
        Tamil: 'Tamil',
        Telugu: 'Telugu',
        Thai: 'Thai',
        Turkish: 'Turkish',
        Ukrainian: 'Ukrainian',
        Urdu: 'Urdu',
        Uzbek: 'Uzbek',
        Vietnamese: 'Vietnamese',
        Yoruba: 'Yoruba',
        Zulu: 'Zulu',
      },
      fluency: {
        'Elementary Proficiency': 'Elementary Proficiency',
        'Limited Working Proficiency': 'Limited Working Proficiency',
        'Minimum Professional Proficiency': 'Minimum Professional Proficiency',
        'Full Professional Proficiency': 'Full Professional Proficiency',
        'Native or Bilingual Proficiency': 'Native or Bilingual Proficiency',
      },
      sections: {
        awards: 'Awards',
        basics: 'Basics',
        certificates: 'Certificates',
        education: 'Education',
        interests: 'Interests',
        languages: 'Languages',
        projects: 'Projects',
        publications: 'Publications',
        references: 'References',
        skills: 'Skills',
        volunteer: 'Volunteer',
        work: 'Work',
      },
      skills: {
        Novice: 'Novice',
        Beginner: 'Beginner',
        Intermediate: 'Intermediate',
        Advanced: 'Advanced',
        Expert: 'Expert',
        Master: 'Master',
      },
    },
    'zh-hans': {
      countries: SimplifiedChineseCountryNames,
      degrees: {
        'Middle School': '初中',
        'High School': '高中',
        Diploma: '专科',
        Associate: '副学士',
        Bachelor: '学士',
        Master: '硕士',
        Doctor: '博士',
      },
      languages: {
        Afrikaans: '南非语',
        Albanian: '阿尔巴尼亚语',
        Amharic: '阿姆哈拉语',
        Arabic: '阿拉伯语',
        Azerbaijani: '阿塞拜疆语',
        Belarusian: '白俄罗斯语',
        Bengali: '孟加拉语',
        Bhojpuri: '博杰普尔语',
        Bulgarian: '保加利亚语',
        Burmese: '缅甸语',
        Cantonese: '粤语',
        Catalan: '加泰罗尼亚语',
        Chinese: '中文',
        Croatian: '克罗地亚语',
        Czech: '捷克语',
        Danish: '丹麦语',
        Dutch: '荷兰语',
        English: '英语',
        Estonian: '爱沙尼亚语',
        Farsi: '波斯语',
        Filipino: '菲律宾语',
        Finnish: '芬兰语',
        French: '法语',
        German: '德语',
        Greek: '希腊语',
        Gujarati: '古吉拉特语',
        Hausa: '豪萨语',
        Hebrew: '希伯来语',
        Hindi: '印地语',
        Hungarian: '匈牙利语',
        Icelandic: '冰岛语',
        Igbo: '伊博语',
        Indonesian: '印度尼西亚语',
        Irish: '爱尔兰语',
        Italian: '意大利语',
        Japanese: '日语',
        Javanese: '爪哇语',
        Kazakh: '哈萨克语',
        Khmer: '高棉语',
        Korean: '韩语',
        Lahnda: '拉亨达语',
        Latvian: '拉脱维亚语',
        Lithuanian: '立陶宛语',
        Malay: '马来语',
        Mandarin: '普通话',
        Marathi: '马拉地语',
        Nepali: '尼泊尔语',
        Norwegian: '挪威语',
        Oromo: '奥罗莫语',
        Pashto: '普什图语',
        Polish: '波兰语',
        Portuguese: '葡萄牙语',
        Romanian: '罗马尼亚语',
        Russian: '俄语',
        Serbian: '塞尔维亚语',
        Shona: '绍纳语',
        Sinhala: '僧伽罗语',
        Slovak: '斯洛伐克语',
        Slovene: '斯洛文尼亚语',
        Somali: '索马里语',
        Spanish: '西班牙语',
        Sundanese: '巽他语',
        Swahili: '斯瓦希里语',
        Swedish: '瑞典语',
        Tagalog: '他加禄语',
        Tamil: '泰米尔语',
        Telugu: '泰卢固语',
        Thai: '泰语',
        Turkish: '土耳其语',
        Ukrainian: '乌克兰语',
        Urdu: '乌尔都语',
        Uzbek: '乌兹别克语',
        Vietnamese: '越南语',
        Yoruba: '约鲁巴语',
        Zulu: '祖鲁语',
      },
      fluency: {
        'Elementary Proficiency': '初级水平',
        'Limited Working Proficiency': '有限工作水平',
        'Minimum Professional Proficiency': '最低专业水平',
        'Full Professional Proficiency': '完全专业水平',
        'Native or Bilingual Proficiency': '母语或双语水平',
      },
      sections: {
        awards: '荣誉',
        basics: '简介',
        certificates: '证书',
        education: '教育背景',
        interests: '兴趣爱好',
        languages: '语言',
        projects: '项目',
        publications: '出版刊物',
        references: '引荐',
        skills: '专业技能',
        volunteer: '志愿服务',
        work: '工作经历',
      },
      skills: {
        Novice: '新手',
        Beginner: '初级',
        Intermediate: '中级',
        Advanced: '高级',
        Expert: '专家',
        Master: '大师',
      },
    },
    'zh-hant-hk': {
      countries: TraditionalChineseCountryHKNames,
      degrees: {
        'Middle School': '初中',
        'High School': '高中',
        Diploma: '專科',
        Associate: '副學士',
        Bachelor: '學士',
        Master: '碩士',
        Doctor: '博士',
      },
      languages: {
        Afrikaans: '南非語',
        Albanian: '阿爾巴尼亞語',
        Amharic: '阿姆哈拉語',
        Arabic: '阿拉伯語',
        Azerbaijani: '阿塞拜疆語',
        Belarusian: '白俄羅斯語',
        Bengali: '孟加拉語',
        Bhojpuri: '博傑普爾語',
        Bulgarian: '保加利亞語',
        Burmese: '緬甸語',
        Cantonese: '粵語',
        Catalan: '加泰羅尼亞語',
        Chinese: '中文',
        Croatian: '克羅地亞語',
        Czech: '捷克語',
        Danish: '丹麥語',
        Dutch: '荷蘭語',
        English: '英語',
        Estonian: '愛沙尼亞語',
        Farsi: '波斯語',
        Filipino: '菲律賓語',
        Finnish: '芬蘭語',
        French: '法語',
        German: '德語',
        Greek: '希臘語',
        Gujarati: '古吉拉特語',
        Hausa: '豪薩語',
        Hebrew: '希伯來語',
        Hindi: '印地語',
        Hungarian: '匈牙利語',
        Icelandic: '冰島語',
        Igbo: '伊博語',
        Indonesian: '印度尼西亞語',
        Irish: '愛爾蘭語',
        Italian: '意大利語',
        Japanese: '日語',
        Javanese: '爪哇語',
        Kazakh: '哈薩克語',
        Khmer: '高棉語',
        Korean: '韓語',
        Lahnda: '拉亨達語',
        Latvian: '拉脫維亞語',
        Lithuanian: '立陶宛語',
        Malay: '馬來語',
        Mandarin: '普通話',
        Marathi: '馬拉地語',
        Nepali: '尼泊爾語',
        Norwegian: '挪威語',
        Oromo: '奧羅莫語',
        Pashto: '普什圖語',
        Polish: '波蘭語',
        Portuguese: '葡萄牙語',
        Romanian: '羅馬尼亞語',
        Russian: '俄語',
        Serbian: '塞爾維亞語',
        Shona: '紹納語',
        Sinhala: '僧伽羅語',
        Slovak: '斯洛伐克語',
        Slovene: '斯洛維尼亞語',
        Somali: '索馬里語',
        Spanish: '西班牙語',
        Sundanese: '巽他語',
        Swahili: '斯瓦希里語',
        Swedish: '瑞典語',
        Tagalog: '他加祿語',
        Tamil: '泰米爾語',
        Telugu: '泰盧固語',
        Thai: '泰語',
        Turkish: '土耳其語',
        Ukrainian: '烏克蘭語',
        Urdu: '烏爾都語',
        Uzbek: '烏茲別克語',
        Vietnamese: '越南語',
        Yoruba: '約魯巴語',
        Zulu: '祖魯語',
      },
      fluency: {
        'Elementary Proficiency': '初級水平',
        'Limited Working Proficiency': '有限工作水平',
        'Minimum Professional Proficiency': '最低專業水平',
        'Full Professional Proficiency': '完全專業水平',
        'Native or Bilingual Proficiency': '母語或雙語水平',
      },
      sections: {
        awards: '榮譽',
        basics: '簡介',
        certificates: '證書',
        education: '教育背景',
        interests: '興趣愛好',
        languages: '語言',
        projects: '項目',
        publications: '出版刊物',
        references: '引薦',
        skills: '專業技能',
        volunteer: '志願服務',
        work: '工作經歷',
      },
      skills: {
        Novice: '新手',
        Beginner: '初級',
        Intermediate: '中級',
        Advanced: '高級',
        Expert: '專家',
        Master: '大師',
      },
    },
    'zh-hant-tw': {
      countries: TraditionalChineseCountryTWNames,
      degrees: {
        'Middle School': '初中',
        'High School': '高中',
        Diploma: '專科',
        Associate: '副學士',
        Bachelor: '學士',
        Master: '碩士',
        Doctor: '博士',
      },
      languages: {
        Afrikaans: '南非語',
        Albanian: '阿爾巴尼亞語',
        Amharic: '阿姆哈拉語',
        Arabic: '阿拉伯語',
        Azerbaijani: '阿塞拜疆語',
        Belarusian: '白俄羅斯語',
        Bengali: '孟加拉語',
        Bhojpuri: '博傑普爾語',
        Bulgarian: '保加利亞語',
        Burmese: '緬甸語',
        Cantonese: '粵語',
        Catalan: '加泰羅尼亞語',
        Chinese: '中文',
        Croatian: '克羅地亞語',
        Czech: '捷克語',
        Danish: '丹麥語',
        Dutch: '荷蘭語',
        English: '英語',
        Estonian: '愛沙尼亞語',
        Farsi: '波斯語',
        Filipino: '菲律賓語',
        Finnish: '芬蘭語',
        French: '法語',
        German: '德語',
        Greek: '希臘語',
        Gujarati: '古吉拉特語',
        Hausa: '豪薩語',
        Hebrew: '希伯來語',
        Hindi: '印地語',
        Hungarian: '匈牙利語',
        Icelandic: '冰島語',
        Igbo: '伊博語',
        Indonesian: '印度尼西亞語',
        Irish: '愛爾蘭語',
        Italian: '意大利語',
        Japanese: '日語',
        Javanese: '爪哇語',
        Kazakh: '哈薩克語',
        Khmer: '高棉語',
        Korean: '韓語',
        Lahnda: '拉亨達語',
        Latvian: '拉脫維亞語',
        Lithuanian: '立陶宛語',
        Malay: '馬來語',
        Mandarin: '普通話',
        Marathi: '馬拉地語',
        Nepali: '尼泊爾語',
        Norwegian: '挪威語',
        Oromo: '奧羅莫語',
        Pashto: '普什圖語',
        Polish: '波蘭語',
        Portuguese: '葡萄牙語',
        Romanian: '羅馬尼亞語',
        Russian: '俄語',
        Serbian: '塞爾維亞語',
        Shona: '紹納語',
        Sinhala: '僧伽羅語',
        Slovak: '斯洛伐克語',
        Slovene: '斯洛維尼亞語',
        Somali: '索馬里語',
        Spanish: '西班牙語',
        Sundanese: '巽他語',
        Swahili: '斯瓦希里語',
        Swedish: '瑞典語',
        Tagalog: '他加祿語',
        Tamil: '泰米爾語',
        Telugu: '泰盧固語',
        Thai: '泰語',
        Turkish: '土耳其語',
        Ukrainian: '烏克蘭語',
        Urdu: '烏爾都語',
        Uzbek: '烏茲別克語',
        Vietnamese: '越南語',
        Yoruba: '約魯巴語',
        Zulu: '祖魯語',
      },
      fluency: {
        'Elementary Proficiency': '初級水平',
        'Limited Working Proficiency': '有限工作水平',
        'Minimum Professional Proficiency': '最低專業水平',
        'Full Professional Proficiency': '完全專業水平',
        'Native or Bilingual Proficiency': '母語或雙語水平',
      },
      sections: {
        awards: '榮譽',
        basics: '簡介',
        certificates: '證書',
        education: '教育背景',
        interests: '興趣愛好',
        languages: '語言',
        projects: '項目',
        publications: '出版刊物',
        references: '引薦',
        skills: '專業技能',
        volunteer: '志願服務',
        work: '工作經歷',
      },
      skills: {
        Novice: '新手',
        Beginner: '初級',
        Intermediate: '中級',
        Advanced: '高級',
        Expert: '專家',
        Master: '大師',
      },
    },
    es: {
      countries: SpanishCountryNames,
      degrees: {
        'Middle School': 'Escuela secundaria',
        'High School': 'Título de secundaria',
        Diploma: 'Diploma',
        Associate: 'Grado de asociado',
        Bachelor: 'Licenciatura',
        Master: 'Maestría',
        Doctor: 'Doctorado',
      },
      languages: {
        Afrikaans: 'Afrikáns',
        Albanian: 'Albanés',
        Amharic: 'Amárico',
        Arabic: 'Árabe',
        Azerbaijani: 'Azerbaiyano',
        Belarusian: 'Bielorruso',
        Bengali: 'Bengalí',
        Bhojpuri: 'Bhojpuri',
        Bulgarian: 'Búlgaro',
        Burmese: 'Birmano',
        Cantonese: 'Cantonés',
        Catalan: 'Catalán',
        Chinese: 'Chino',
        Croatian: 'Croata',
        Czech: 'Checo',
        Danish: 'Danés',
        Dutch: 'Neerlandés',
        English: 'Inglés',
        Estonian: 'Estonio',
        Farsi: 'Persa',
        Filipino: 'Filipino',
        Finnish: 'Finlandés',
        French: 'Francés',
        German: 'Alemán',
        Greek: 'Griego',
        Gujarati: 'Gujarati',
        Hausa: 'Hausa',
        Hebrew: 'Hebreo',
        Hindi: 'Hindi',
        Hungarian: 'Húngaro',
        Icelandic: 'Islandés',
        Igbo: 'Igbo',
        Indonesian: 'Indonesio',
        Irish: 'Irlandés',
        Italian: 'Italiano',
        Japanese: 'Japonés',
        Javanese: 'Javanés',
        Kazakh: 'Kazajo',
        Khmer: 'Jemer',
        Korean: 'Coreano',
        Lahnda: 'Lahnda',
        Latvian: 'Letón',
        Lithuanian: 'Lituano',
        Malay: 'Malayo',
        Mandarin: 'Mandarín',
        Marathi: 'Marathi',
        Nepali: 'Nepalí',
        Norwegian: 'Noruego',
        Oromo: 'Oromo',
        Pashto: 'Pastún',
        Polish: 'Polaco',
        Portuguese: 'Portugués',
        Romanian: 'Rumano',
        Russian: 'Ruso',
        Serbian: 'Serbio',
        Shona: 'Shona',
        Sinhala: 'Cingalés',
        Slovak: 'Eslovaco',
        Slovene: 'Esloveno',
        Somali: 'Somalí',
        Spanish: 'Español',
        Sundanese: 'Sundanés',
        Swahili: 'Swahili',
        Swedish: 'Sueco',
        Tagalog: 'Tagalo',
        Tamil: 'Tamil',
        Telugu: 'Telugu',
        Thai: 'Tailandés',
        Turkish: 'Turco',
        Ukrainian: 'Ucraniano',
        Urdu: 'Urdu',
        Uzbek: 'Uzbeko',
        Vietnamese: 'Vietnamita',
        Yoruba: 'Yoruba',
        Zulu: 'Zulú',
      },
      fluency: {
        'Elementary Proficiency': 'Competencia elemental',
        'Limited Working Proficiency': 'Competencia limitada de trabajo',
        'Minimum Professional Proficiency':
          'Competencia profesional de trabajo',
        'Full Professional Proficiency': 'Competencia profesional plena',
        'Native or Bilingual Proficiency': 'Competencia nativa o bilingüe',
      },
      sections: {
        awards: 'Premios',
        basics: 'Información básica',
        certificates: 'Certificados',
        education: 'Educación',
        interests: 'Intereses',
        languages: 'Idiomas',
        projects: 'Proyectos',
        publications: 'Publicaciones',
        references: 'Referencias',
        skills: 'Competencias',
        volunteer: 'Voluntariado',
        work: 'Experiencia laboral',
      },
      skills: {
        Novice: 'Novato',
        Beginner: 'Principiante',
        Intermediate: 'Intermedio',
        Advanced: 'Avanzado',
        Expert: 'Experto',
        Master: 'Maestro',
      },
    },
  }

  return get(
    optionsTranslations,
    [language, category, option],
    option
  ) as string
}
